/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectVariantDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import { Grid, Box, Typography } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { componentStyle } from "../../../../config/appTheme";
import { useSelector } from "react-redux";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";
import CustomToolTip from "../../../infoButton/CustomToolTip";


const ProjectVariantDialog = ({
  project_status,
  helper,
  selectedVehicleView = "ECU",
}) => {
  const projectStatus = {
    101: "DRAFT",
    100: "ACTIVE PRE-SOP",
    200: "ACTIVE POST-SOP",
    300: "DE-ACTIVATED",
    500: "COMPLETED",
  };
  const styles = {
    accordionWrapper: {
      border: "1px solid #e0e0e0",
      boxShadow: "none",
      "&:before": { display: "none" },
    },
    summary: {
      borderBottom: "none",
      "& .MuiAccordionSummary-content": {
        justifyContent: "space-between",
        alignItems: "center",
      },
    },
    sectionTitle: {
      ...componentStyle.font.cardHeadder,
      fontWeight: 600,
    },
    detailGridItem: {
      display: "flex",
      alignItems: "left",
    },
    labelText: {
      ...componentStyle.font.cardText,
      fontWeight: 600,
      mr: 0.5,
      display: "inline-block",
    },
    valueText: { display: "inline-block", ...componentStyle.font.text },
  };

  const projectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const variantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails
  );

  const [openProjectVariantDetails, setOpenProjectVariantDetails] =
    useState(false);

  return (
    <Box>
      <Box
        onClick={() => {
          setOpenProjectVariantDetails(true);
        }}
      >
        <VisibilityOutlinedIcon />
      </Box>

      <CustomDialogBoxV2
        open={openProjectVariantDetails}
        onClose={() => {
          setOpenProjectVariantDetails(false);
        }}
      >
        <Grid container spacing={1} sx={{ width: "450px" }}>
          {/* 🔹 Project Configurations */}
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Typography sx={styles.sectionTitle}>
                Project Configurations
              </Typography>
            </Box>
            <Grid container spacing={0}>
              <Grid item sm={12}>
                <Box sx={{ ...styles.detailGridItem }}>
                  <Typography sx={{ ...styles.labelText }}>Name:</Typography>
                  <CustomToolTip length={40}>
                    {projectDetails?.name || "-"}
                  </CustomToolTip>
                </Box>
              </Grid>
              {[
                { label: "Trade Mark", value: projectDetails?.trade_mark },
                {
                  label: "Architecture",
                  value: projectDetails?.architecture_name,
                },
                { label: "Platform", value: projectDetails?.vehicle_platform },
                {
                  label: "Status",
                  value: projectStatus[projectDetails?.status],
                },
              ].map((item, idx) => (

                <Grid item xs={12} sm={6} key={idx}>
                  <Box sx={styles.detailGridItem}>
                    <Typography sx={styles.labelText}>{item.label}:</Typography>
                    <CustomToolTip length={15}>
                      {item.value || "-"}
                    </CustomToolTip>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* 🔹 Variant Configurations */}
          <Grid item xs={12} md={12}>
            <Grid container spacing={0}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Typography sx={styles.sectionTitle}>
                  Variant Configurations
                </Typography>
              </Box>
              {[
                { label: "Name", value: variantDetails?.name },
                {
                  label: "Powertrain",
                  value: variantDetails?.powertrain_class_details?.[0]?.name,
                },
                { label: "Market", value: variantDetails?.delivery_market },
                { label: "Status", value: variantDetails?.vehicle_status },
              ].map((item, idx) => (

                <Grid item xs={12} sm={6} key={idx}>
                  <Box sx={styles.detailGridItem}>
                    <Typography sx={styles.labelText}>{item.label}:</Typography>
                    <CustomToolTip length={15}>
                      {item.value || "-"}
                    </CustomToolTip>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </CustomDialogBoxV2>
    </Box>
  );
};

export default ProjectVariantDialog;
